import { Workflow } from 'lucide-react';
import { percent, type PipelineStage } from '../stats';
import { QB_PROCESS_MODES } from '@/pages/WorkflowV2/qbProcess';
import {
  QB_PROCESS_MODE_LABELS,
  type QbProcessMode,
} from '@/pages/WorkflowV2/components/StatusFilterChips';
import WidgetCard, { EmptyLine } from './WidgetCard';

interface PipelineCardProps {
  stages: PipelineStage[];
  total: number;
  /** e.g. "September 2026" */
  booksLabel: string;
  loading?: boolean;
  onOpen: () => void;
}

/** Bar segment per QB Process bucket, same colors on every bar */
const PROCESS_COLORS: Record<QbProcessMode, string> = {
  manual: 'bg-indigo-500',
  online: 'bg-sky-400',
  none: 'bg-slate-400',
};

/**
 * Funnel of the month's bookkeeping milestones: of the Bookkeeping
 * accounts, how many have statements downloaded, statements booked, manual
 * receipts booked, and passed prelim and final review for the books month.
 * Each stage's `waiting` (computed in stats.ts) is the work still sitting
 * at that step. Every bar is stacked by the accounts' QB Process (Manual /
 * Online / None), with the split spelled out under the bar.
 *
 * In My work (stages carry `mine`), each bar is instead the employee's
 * share of that step: stamps they made out of the team's.
 */
export default function PipelineCard({
  stages,
  total,
  booksLabel,
  loading,
  onOpen,
}: Readonly<PipelineCardProps>) {
  const mineView = stages.some(s => s.mine !== null);
  return (
    <WidgetCard
      title="Bookkeeping Pipeline"
      icon={Workflow}
      loading={loading}
      action={{ label: 'Open Bookkeeping', onClick: onOpen }}
    >
      {total === 0 ? (
        <EmptyLine>No Bookkeeping accounts this month.</EmptyLine>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs text-slate-500">
            <p>
              {mineView
                ? `${booksLabel} books: steps you stamped, out of the team's`
                : `${booksLabel} books, ${total} Bookkeeping clients`}
            </p>
            <ul
              className="flex items-center gap-3 text-[11px]"
              aria-label="QB Process legend"
            >
              {QB_PROCESS_MODES.map(mode => (
                <li key={mode} className="flex items-center gap-1">
                  <span
                    aria-hidden
                    className={`inline-block h-2 w-2 rounded-sm ${PROCESS_COLORS[mode]}`}
                  />
                  {QB_PROCESS_MODE_LABELS[mode]}
                </li>
              ))}
            </ul>
          </div>
          <ol className="space-y-3">
            {stages.map(stage => {
              const value = stage.mine ?? stage.count;
              const max = stage.mine === null ? total : stage.count;
              const pct = percent(value, max);
              const waiting = stage.waiting ?? 0;
              return (
                <li key={stage.key}>
                  <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
                    <span className="min-w-0">
                      <span className="font-medium text-slate-700">
                        {stage.label}
                      </span>
                      {stage.description && (
                        <span className="block text-[11px] text-slate-500">
                          {stage.description}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 tabular-nums text-slate-600">
                      <span className="font-semibold text-slate-900">
                        {value}
                      </span>
                      {stage.mine !== null && <> of {stage.count}</>} ({pct}%)
                      {waiting > 0 && (
                        <span className="ml-1 text-amber-700">
                          · {waiting} waiting
                        </span>
                      )}
                    </span>
                  </div>
                  <div
                    className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100"
                    role="progressbar"
                    aria-label={stage.label}
                    aria-valuenow={value}
                    aria-valuemin={0}
                    aria-valuemax={max}
                  >
                    {QB_PROCESS_MODES.map(mode => (
                      <div
                        key={mode}
                        className={`h-full transition-all duration-500 ${PROCESS_COLORS[mode]}`}
                        style={{
                          width: `${max > 0 ? (stage.byProcess[mode] / max) * 100 : 0}%`,
                        }}
                        title={`${QB_PROCESS_MODE_LABELS[mode]}: ${stage.byProcess[mode]}`}
                      />
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] tabular-nums text-slate-500">
                    {QB_PROCESS_MODES.map((mode, j) => (
                      <span key={mode}>
                        {j > 0 && ' · '}
                        {QB_PROCESS_MODE_LABELS[mode]}{' '}
                        <span className="font-medium text-slate-700">
                          {stage.byProcess[mode]}
                        </span>
                      </span>
                    ))}
                  </p>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </WidgetCard>
  );
}
